import clsx from 'clsx';
import { PrimaryButton } from 'Components/Buttons';
import { Box, Form, Heading, Layer, Text } from 'grommet';
import { XIcon } from 'lucide-preact';
import { h } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { connect } from 'react-redux';
import { API_BASE_URL } from 'Shared/fetch';
import useFetch from 'use-http';

import { getPostAuthDestination } from '@/shared/postAuthDestination';

import { emailRegex } from '../../../helpers';
import { useIsMobileSize } from '../../../hooks';
import style from '../style.scss';
import { enableSubmit, showMagicLinkModal } from './actions';
import modalStyle from './modalStyle.scss';

const MagicLinkModal = ({
  showMagicLinkModal,
  submitEnabled,
  showMagicLinkModalAction,
  enableSubmitAction,
  email: initialEmail,
}) => {
  const [email, setEmail] = useState(initialEmail);
  const [hasError, setHasError] = useState('');
  const [sent, setSent] = useState();
  const isSmallScreen = useIsMobileSize();
  const { loading, error, post, response } = useFetch(API_BASE_URL, { cachePolicy: 'no-cache' });

  useEffect(() => {
    setEmail(initialEmail);
  }, [initialEmail]);

  if (!showMagicLinkModal) {
    return null;
  }

  const closeHandler = () => {
    setSent();
    setEmail('');
    setHasError('');
    showMagicLinkModalAction(false);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSent(false);
    setHasError('');

    if (!email) {
      setHasError('Email is required!');
      return;
    }

    if (!email.match(emailRegex)) {
      setHasError('Invalid email format!');
      return;
    }
    try {
      // Where they were going, so the link can take them there. The mail client may open it in a
      // tab — or a browser — that shares nothing with this one, so the link is the only thing
      // guaranteed to carry it.
      await post('/users/sendMagicLoginEmail', { email, redirect: getPostAuthDestination() ?? undefined });
    } catch (err) {
      console.error(err);
    }
    if (response.ok) {
      setSent(true);
    }
  };

  const onEmailChange = ({ target }) => {
    setHasError('');
    setEmail(target.value);
    enableSubmitAction(!!target.value.match(emailRegex));
  };

  return (
    <Layer className={modalStyle.container} onEsc={closeHandler} onClickOutside={closeHandler}>
      <button onClick={closeHandler} type="button" className={modalStyle.close}>
        <XIcon className="size-[1em]" />
      </button>
      <Box align="center" width="290px" margin={{ vertical: '30px', horizontal: isSmallScreen ? '22px' : '50px' }}>
        <Heading level="3" responsive>
          Want Instant Access?
        </Heading>
        <Text textAlign="center" margin={{ bottom: '40px' }} size="large" color="var(--charcoalLight)" as="div">
          Get a magic link in your inbox to log in instantly, no password required!
        </Text>
        <Form onSubmit={onSubmit}>
          <Box align="start" className={modalStyle.inputContainer}>
            <span className={modalStyle.label}>Your Email:</span>
            <Box margin={{ bottom: '5px' }} className={modalStyle.inputWrapper}>
              <input
                type="text"
                placeholder="Email"
                value={email}
                onChange={onEmailChange}
                className={clsx(modalStyle.input, { [style.errorBorder]: hasError })}
              />
            </Box>
            <div className={clsx(style.errorSection, { [style.active]: hasError || error })}>
              {hasError}
              {error && response.data.error && response.data.error}
              {!error && !hasError && sent && (
                <Text
                  textAlign="center"
                  margin={{ bottom: '20px', top: '20px' }}
                  size="large"
                  color="var(--primaryGreen)"
                  as="div"
                >
                  {response.data.message}
                </Text>
              )}
            </div>
            <br />
            <Box alignContent="center" margin={{ bottom: '15px', horizontal: 'auto' }}>
              <PrimaryButton
                loading={loading}
                type="submit"
                disabled={!submitEnabled}
                size="large"
                label={!sent ? 'Email Magic Link' : 'Resend'}
              />
            </Box>
          </Box>
        </Form>
      </Box>
    </Layer>
  );
};

export default connect(
  ({ magicLink: { showMagicLinkModal, submitEnabled, email } }) => ({
    showMagicLinkModal,
    submitEnabled,
    email,
  }),
  {
    showMagicLinkModalAction: showMagicLinkModal,
    enableSubmitAction: enableSubmit,
  },
)(MagicLinkModal);
